import React, { useEffect } from 'react';
import SecureLudoGame from './components/Ludo/SecureLudoGame';
import SecureNaijaLudoGame from './components/Ludo/SecureNaijaLudoGame';
import GameSelector from './components/GameSelector';
import GameSetup from './components/GameSetup';
import Dashboard from './components/Dashboard/Dashboard';
import LoginScreen from './components/Auth/LoginScreen';
import DebugPanel from './components/Debug/DebugPanel';

// Custom hooks
import { useAuth } from './hooks/useAuth';
import { usePiSDK } from './hooks/usePiSDK';
import { useDebugLogs } from './hooks/useDebugLogs';
import { useGameNavigation } from './hooks/useGameNavigation';

import './App.css';
import './components/CharacterSelection.css';

// Pi Network SDK types
declare global {
  interface Window {
    Pi: {
      init: (config: { version: string; sandbox: boolean }) => void;
      authenticate: (
        scopes: string[],
        onIncompletePaymentFound?: (payment: any) => void
      ) => Promise<{
        accessToken: string;
        user: {
          uid: string;
          username: string;
        };
      }>;
      createPayment: (paymentData: any, callbacks: any) => void;
    };
  }
}

function App() {
  // Custom hooks
  const auth = useAuth();
  const { debugLogs, addDebugLog, clearDebugLogs } = useDebugLogs();
  const navigation = useGameNavigation();

  // Pi SDK hook
  const { handleRetryPiSDK } = usePiSDK({
    piSDKLoaded: auth.piSDKLoaded,
    setPiSDKLoaded: auth.setPiSDKLoaded,
    setError: auth.setError,
    addDebugLog,
  });

  // Log current mode for debugging
  useEffect(() => {
    const mode = auth.isDevelopment
      ? 'DEVELOPMENT'
      : auth.isSemiProduction
        ? 'SEMI-PRODUCTION'
        : 'PRODUCTION';
    addDebugLog(`🚀 App Mode: ${mode}`);
    addDebugLog(`🌐 Hostname: ${window.location.hostname}`);
    addDebugLog(`🔧 Semi-prod env: ${process.env.REACT_APP_SEMI_PRODUCTION}`);
  }, [auth.isDevelopment, auth.isSemiProduction, addDebugLog]);

  // Game action handlers that use both hooks
  const handleQuickPlay = () => {
    navigation.handleQuickPlay(addDebugLog, auth.setError);
  };

  const handleComputerGame = (difficulty: 'easy' | 'medium' | 'hard') => {
    navigation.handleComputerGame(difficulty, addDebugLog, auth.setError);
  };

  // Show Game Selector if selected
  if (auth.isAuthenticated && navigation.currentView === 'game-selector') {
    return (
      <div className="App">
        <DebugPanel
          debugLogs={debugLogs}
          clearDebugLogs={clearDebugLogs}
          piSDKLoaded={auth.piSDKLoaded}
        />
        <div className="game-header-nav">
          <button
            onClick={navigation.handleBackToDashboard}
            className="back-btn"
          >
            ← Back to Dashboard
          </button>
          <button onClick={auth.handleSignOut} className="sign-out-btn">
            Sign Out
          </button>
        </div>
        <GameSelector onGameSelect={navigation.handleGameSelect} />
      </div>
    );
  }

  // Show Game Setup if selected
  if (auth.isAuthenticated && navigation.currentView === 'game-setup') {
    return (
      <div className="App">
        <DebugPanel
          debugLogs={debugLogs}
          clearDebugLogs={clearDebugLogs}
          piSDKLoaded={auth.piSDKLoaded}
        />
        <div className="game-header-nav">
          <button
            onClick={navigation.handleBackFromGameSetup}
            className="back-btn"
          >
            ← Back to Games
          </button>
          <button onClick={auth.handleSignOut} className="sign-out-btn">
            Sign Out
          </button>
        </div>
        <GameSetup
          gameType={navigation.selectedGameType}
          onQuickPlay={handleQuickPlay}
          onComputerGame={handleComputerGame}
          onBack={navigation.handleBackFromGameSetup}
        />
      </div>
    );
  }

  // Show Classic Ludo game if selected
  if (auth.isAuthenticated && navigation.currentView === 'ludo') {
    return (
      <div className="App game-active">
        <DebugPanel
          debugLogs={debugLogs}
          clearDebugLogs={clearDebugLogs}
          piSDKLoaded={auth.piSDKLoaded}
        />
        <div className="game-header-nav">
          <button
            onClick={navigation.handleBackToGameSelector}
            className="back-btn"
          >
            ← Back to Games
          </button>
          <button onClick={auth.handleSignOut} className="sign-out-btn">
            Sign Out
          </button>
        </div>
        <SecureLudoGame gameId={navigation.currentGameId} />
      </div>
    );
  }

  // Show Naija Ludo game if selected
  if (auth.isAuthenticated && navigation.currentView === 'naija-ludo') {
    return (
      <div className="App game-active">
        <DebugPanel
          debugLogs={debugLogs}
          clearDebugLogs={clearDebugLogs}
          piSDKLoaded={auth.piSDKLoaded}
        />
        <div className="game-header-nav">
          <button
            onClick={navigation.handleBackToGameSelector}
            className="back-btn"
          >
            ← Back to Games
          </button>
          <button onClick={auth.handleSignOut} className="sign-out-btn">
            Sign Out
          </button>
        </div>
        <SecureNaijaLudoGame gameId={navigation.currentGameId} />
      </div>
    );
  }

  // Show Dashboard if authenticated
  if (auth.isAuthenticated && auth.user) {
    return (
      <>
        <DebugPanel
          debugLogs={debugLogs}
          clearDebugLogs={clearDebugLogs}
          piSDKLoaded={auth.piSDKLoaded}
        />
        <Dashboard
          user={auth.user}
          onPlayGames={navigation.handlePlayGames}
          onSignOut={auth.handleSignOut}
        />
      </>
    );
  }

  // Show Login Screen if not authenticated
  return (
    <>
      <DebugPanel
        debugLogs={debugLogs}
        clearDebugLogs={clearDebugLogs}
        piSDKLoaded={auth.piSDKLoaded}
      />
      <LoginScreen
        loading={auth.loading}
        error={auth.error}
        piSDKLoaded={auth.piSDKLoaded}
        isDevelopment={auth.isDevelopment}
        isSemiProduction={auth.isSemiProduction}
        isProduction={auth.isProduction}
        onPiSignIn={auth.handlePiSignIn}
        onDevBypass={auth.handleDevBypass}
        onSemiProdAuth={auth.handleSemiProdAuth}
        onRetryPiSDK={handleRetryPiSDK}
      />
    </>
  );
}

export default App;
